import { useEffect, useRef } from "react";
import { Box, Heading, HStack, Flex, Text, chakra } from "@chakra-ui/react";
import { trackAppDownloadClick } from "../core/analytics";

import Poster from "../assets/images/poster.png";

export const Testimonials = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const node = videoRef.current;

    if (!node || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = entry.target as HTMLVideoElement;

        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      {
        threshold: 0.6,
      },
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      h={{ base: "auto", lg: "100vh" }}
      pb={{ base: "48px", lg: 0 }}
      pos="relative"
      overflow="hidden" // clip gradients so they don't bleed outside layout
      maxW={{ base: "100%", "2xl": "1700px" }}
      mx="auto"
    >
      <Box
        position="absolute"
        top={0}
        left={-300}
        width="30%"
        height="100%"
        zIndex={0}
        opacity={0.6}
        bg="radial-gradient(circle, #8FD3D6 0%, #EEF4F200 70%)"
        filter="blur(144px)"
        pointerEvents="none" // don't block scroll/interaction
      />

      <Box
        position="absolute"
        top={0}
        right={-300}
        width="30%"
        height="100%"
        zIndex={0}
        opacity={0.6}
        bg="radial-gradient(circle, #FF8E3C 0%, #EEF4F200 70%)"
        filter="blur(144px)"
        pointerEvents="none" // don't block scroll/interaction
      />

      <Flex
        flexDir={{ base: "column", lg: "row" }}
        gap={{ base: "48px", lg: "120px" }}
        mt="53px"
        h={"-webkit-fill-available"}
        px={{ base: "16px", lg: "200px" }}
        overflowX={{ base: "hidden", lg: "scroll" }}
        overflowY={{ base: "auto", lg: "hidden" }}
        align={{ base: "stretch", lg: "center" }}
        position="relative"
        zIndex={1} // ensure content is above the gradient overlays
        css={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Box w={{ base: "100%", lg: "496px" }} flex="none">
          <Flex flexDir={"column"} align={{ base: "center", lg: "flex-start" }}>
            <Heading
              fontSize={{ base: "28px", md: "48px", lg: "64px" }}
              textAlign={{ base: "center", lg: "left" }}
              color={"#19202D"}
              lineHeight={"100%"}
              fontFamily={"LeagueSpartan"}
            >
              Why you should get Book-d
            </Heading>

            <Text
              fontSize={{ base: "16px", md: "18px", lg: "24px" }}
              mt="10px"
              mb="20px"
              color={"#19202D"}
              lineHeight={"140%"}
              textAlign={{ base: "center", lg: "left" }}
            >
              Don't listen to us, hear directly from professionals on Book-d!
            </Text>

            <HStack gap={"24px"}>
              <Flex
                borderRadius={"30px"}
                borderWidth={"1px"}
                borderColor={"#3C4257"}
                py="4px"
                px="15px"
                bg="transparent"
                gap={"10px"}
                align={"center"}
                cursor={"pointer"}
                onClick={() => {
                  trackAppDownloadClick("app_store", "stylist");
                  window.open("https://apps.apple.com/us/app/bookd-pro/id6503949063", "_blank");
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M9.75252 4.67813C9.77681 3.92445 9.98441 2.81417 10.7069 2.08476C11.3776 1.4077 12.194 1.07771 12.7025 0.926853C12.8988 0.868613 13.0872 1.01665 13.0775 1.22119C13.0435 1.94269 12.8537 3.01885 12.3643 3.59537C11.9028 4.13883 11.1606 4.85951 10.0733 4.98077C9.89535 5.00061 9.74675 4.8571 9.75252 4.67813Z"
                    fill="#3C4257"
                  />
                  <path
                    d="M11.6142 18.473C12.1363 18.673 12.6642 18.8753 13.1419 18.8753C14.1639 18.8753 17.1398 16.5173 17.247 14.0683C17.2525 13.9425 17.1774 13.8302 17.0675 13.7687C16.3106 13.3451 15.0744 12.2904 15.0314 10.756C14.9813 8.96774 15.8791 7.54742 16.5586 6.9599C16.7116 6.82767 16.7641 6.59452 16.6276 6.44542C16.0093 5.77025 14.7655 4.84154 13.6307 4.86433C12.8051 4.88092 12.0612 5.18846 11.4229 5.45237C10.9332 5.65483 10.5056 5.83162 10.1508 5.83162C9.81486 5.83162 9.41152 5.65597 8.94859 5.45437C8.32516 5.18288 7.59367 4.86433 6.77311 4.86433C5.25316 4.86433 2.25001 6.12473 2.25 10.9904C2.25 13.5699 4.9128 18.9436 7.15976 18.8753C7.64495 18.8753 8.16348 18.6743 8.67802 18.475C9.18471 18.2787 9.68753 18.0838 10.1508 18.0838C10.5982 18.0838 11.1034 18.2774 11.6142 18.473Z"
                    fill="#3C4257"
                  />
                </svg>

                <Text
                  fontSize={{ base: "16px", md: "18px", lg: "20px" }}
                  fontWeight={500}
                  color="#3C4257"
                  fontFamily={"LeagueSpartan"}
                >
                  App Store
                </Text>
              </Flex>

              <Flex
                borderRadius={"30px"}
                borderWidth={"1px"}
                borderColor={"#3C4257"}
                py="4px"
                px="15px"
                bg="transparent"
                gap={"10px"}
                align={"center"}
                cursor={"pointer"}
                onClick={() => {
                  trackAppDownloadClick("play_store", "stylist");
                  window.open("https://play.google.com/store/apps/details?id=com.book_d&pcampaignid=web_share", "_blank");
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5.81163 16.6433C5.74361 16.7113 5.82501 16.8216 5.90955 16.7759C8.06639 15.6092 10.9784 14.033 13.4393 12.6964C13.4885 12.6697 13.4975 12.6033 13.4577 12.564L11.7236 10.8566C11.691 10.8245 11.6386 10.8247 11.6062 10.857L5.81163 16.6433Z"
                    fill="#3C4257"
                  />
                  <path
                    d="M11.6009 8.97848C11.6334 9.01076 11.686 9.01065 11.7184 8.97824L13.4532 7.24504C13.4927 7.2056 13.483 7.13926 13.4339 7.11275L12.5839 6.65392C11.5619 6.10228 10.1924 5.36319 8.80248 4.61335C7.7616 4.05179 6.71035 3.48479 5.78522 2.98609C5.70051 2.94042 5.61872 3.05089 5.68708 3.11863L11.6009 8.97848Z"
                    fill="#3C4257"
                  />
                  <path
                    d="M2.5 2.66197C2.5 2.46837 2.52929 2.3063 2.58024 2.17279C2.63289 2.03482 2.80668 2.02425 2.91158 2.12819L10.7154 9.86088C10.7483 9.89341 10.7483 9.94645 10.7156 9.97908L2.88338 17.7881C2.768 17.9031 2.57728 17.8753 2.54038 17.7166C2.51359 17.6014 2.5 17.4736 2.5 17.3364V2.66197Z"
                    fill="#3C4257"
                  />
                  <path
                    d="M14.7401 11.989C14.7079 12.0065 14.668 12.0009 14.6419 11.9752L12.6103 9.97538C12.5773 9.94285 12.5771 9.8896 12.61 9.8569L14.6526 7.82628C14.6787 7.80028 14.7191 7.7947 14.7515 7.81236C16.49 8.76056 17.8463 9.49677 18.2597 9.71551C18.2752 9.72369 18.2872 9.73606 18.2942 9.75212C18.3319 9.8391 18.3406 9.92562 18.3278 10.0089C18.3241 10.0329 18.3084 10.0527 18.2869 10.0641C18.0491 10.1894 17.2528 10.6225 16.1436 11.2258L16.1368 11.2295C15.7123 11.4604 15.2422 11.7161 14.7401 11.989Z"
                    fill="#3C4257"
                  />
                </svg>

                <Text
                  fontSize={{ base: "16px", md: "18px", lg: "20px" }}
                  fontWeight={500}
                  color="#3C4257"
                  fontFamily={"LeagueSpartan"}
                >
                  Google Play
                </Text>
              </Flex>
            </HStack>
          </Flex>
        </Box>

        <Box
          justifySelf={"center"}
          alignContent={"center"}
          pos={"relative"}
          flex={{ base: "none", lg: "none" }}
          w={{ base: "100%", lg: "auto" }}
          display="flex"
          justifyContent="center"
        >
          <chakra.video
            ref={videoRef}
            src={"https://admin.dev.book-d.co.uk/uploads/testimonial/Phebe.mov"}
            poster={Poster}
            w={{ base: "100%", lg: "auto" }}
            h={{ base: "auto", lg: "70vh" }}
            maxW={{ base: "100%", lg: "auto" }}
            objectFit={{ base: "cover", lg: "contain" }}
            borderRadius={{ base: "32px", lg: "0" }}
            loop
            playsInline
            controls
          />
        </Box>

        <Box w={{ base: "100%", lg: "496px" }} flex="none">
          <Text
            fontSize={{ base: "20px", md: "24px", lg: "31px" }}
            color={"#19202D"}
            fontFamily={"LeagueSpartan"}
            lineHeight={"100%"}
            textAlign={{ base: "center", lg: "left" }}
          >
            …As a small business owner, I know I can focus on my business
            knowing that Book-d will consistently promote my business and draw
            in clients even on the days I don't feel like creating content…
          </Text>
        </Box>

        {/* <Box
          justifySelf={"center"}
          alignContent={"center"}
          pos={"relative"}
          flex={{ base: "none", lg: "none" }}
          w={{ base: "100%", lg: "auto" }}
          display="flex"
          justifyContent="center"
        >
          <Image
            src={Story2}
            w={{ base: "100%", lg: "auto" }}
            h={{ base: "auto", lg: "70vh" }}
            maxW={{ base: "100%", lg: "auto" }}
            objectFit={{ base: "cover", lg: "contain" }}
            borderRadius={{ base: "32px", lg: "0" }}
          />
        </Box>

        <Box w={{ base: "100%", lg: "496px" }} flex="none">
          <Text
            fontSize={{ base: "20px", md: "24px", lg: "31px" }}
            color={"#19202D"}
            fontFamily={"LeagueSpartan"}
            lineHeight={"100%"}
            textAlign={{ base: "center", lg: "left" }}
          >
            “Book-d has been great with helping me manage my business as a
            student and part-time braider.”
          </Text>
        </Box>

        <Box
          justifySelf={"center"}
          alignContent={"center"}
          pos={"relative"}
          flex={{ base: "none", lg: "none" }}
          w={{ base: "100%", lg: "auto" }}
          display="flex"
          justifyContent="center"
        >
          <Image
            src={Story3}
            w={{ base: "100%", lg: "auto" }}
            h={{ base: "auto", lg: "70vh" }}
            maxW={{ base: "100%", lg: "auto" }}
            objectFit={{ base: "cover", lg: "contain" }}
            borderRadius={{ base: "32px", lg: "0" }}
          />
        </Box> */}
      </Flex>
    </Box>
  );
};
